import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { ymd } from "@/components/planning/utils";
import { normalizeAnchor, selRange, type CmpMode, type DayRange, type PeriodMode, type PeriodSel } from "@/lib/period-nav";

interface PeriodState extends PeriodSel {
  setPeriod: (p: PeriodMode) => void;
  setCmp: (c: CmpMode) => void;
  /** Jour d'ancrage AAAA-MM-JJ ; `null` = période en cours (voir lib/period-nav). */
  setDate: (d: string | null) => void;
  setRange: (r: DayRange) => void;
  /** Remplace toute la sélection (raccourcis, calendrier, lien partagé). */
  apply: (s: Partial<PeriodSel>) => void;
}

const Ctx = createContext<PeriodState | null>(null);

/** Une seule comparaison pour l'année (l'année précédente). */
function normalize(s: PeriodSel): PeriodSel {
  const period = s.period === "perso" && !s.range ? "jour" : s.period;
  return {
    period,
    date: period === "perso" ? null : normalizeAnchor(period, s.date),
    range: period === "perso" ? s.range : null,
    cmp: period === "annee" ? "prev" : s.cmp,
  };
}

/** Période (Jour … Année ou plage libre), jour d'ancrage et comparaison partagés par tous les écrans. */
export function PeriodProvider({ children }: { children: ReactNode }) {
  const [sel, setSel] = useState<PeriodSel>({ period: "jour", date: null, range: null, cmp: "prev" });
  const apply = useCallback((s: Partial<PeriodSel>) => setSel((cur) => normalize({ ...cur, ...s })), []);
  // Changer de période garde le jour d'ancrage : hier en « Mois » est ce mois-ci.
  // « Période… » part de la plage affichée, ramenée à aujourd'hui au plus tard.
  const setPeriod = useCallback(
    (p: PeriodMode) =>
      setSel((cur) => {
        if (p !== "perso") return normalize({ ...cur, period: p, date: cur.period === "perso" ? cur.range?.to ?? null : cur.date });
        const r = selRange(cur);
        const max = new Date();
        const to = r.to > max ? max : r.to;
        return normalize({ ...cur, period: "perso", range: { from: ymd(r.from), to: ymd(to) } });
      }),
    []
  );
  const value = useMemo(
    () => ({
      ...sel,
      apply,
      setPeriod,
      setCmp: (cmp: CmpMode) => apply({ cmp }),
      setDate: (date: string | null) => apply({ date }),
      setRange: (range: DayRange) => apply({ period: "perso", range }),
    }),
    [sel, apply, setPeriod]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePeriod(): PeriodState {
  const v = useContext(Ctx);
  if (!v) throw new Error("PeriodProvider manquant");
  return v;
}

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const PERIODS: PeriodMode[] = ["jour", "semaine", "mois", "annee", "perso"];

function fromParams(p: URLSearchParams): PeriodSel | null {
  const period = p.get("period") as PeriodMode | null;
  if (!period || !PERIODS.includes(period)) return null;
  const date = p.get("date");
  const from = p.get("from");
  const to = p.get("to");
  return normalize({
    period,
    date: date && DAY.test(date) ? date : null,
    range: from && to && DAY.test(from) && DAY.test(to) && from <= to ? { from, to } : null,
    cmp: p.get("cmp") === "n1" ? "n1" : "prev",
  });
}

function toParams(s: PeriodSel): Record<string, string> {
  const out: Record<string, string> = { period: s.period, cmp: s.cmp };
  if (s.date) out.date = s.date;
  if (s.range) {
    out.from = s.range.from;
    out.to = s.range.to;
  }
  return out;
}

const KEYS = ["period", "date", "from", "to", "cmp"];

/**
 * Garde la sélection dans l'URL (`?period=jour&date=…&cmp=prev`,
 * `?period=perso&from=…&to=…`) : lien partageable et retour navigateur.
 * À monter sur les pages qui affichent la barre de période.
 */
export function usePeriodUrlSync() {
  const s = usePeriod();
  const [params, setParams] = useSearchParams();
  const fromUrl = fromParams(params);
  const urlKey = fromUrl ? JSON.stringify(toParams(fromUrl)) : null;
  const stateKey = JSON.stringify(toParams(s));
  // Sélection à l'arrivée sur la page : tant que l'URL n'a pas été appliquée, on ne l'écrase pas.
  const arrival = useRef<string | null>(stateKey);

  // URL → état : arrivée par un lien, ou retour navigateur.
  useEffect(() => {
    if (fromUrl && urlKey !== stateKey) s.apply(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlKey]);

  // État → URL : chaque changement ajoute une entrée d'historique.
  useEffect(() => {
    if (stateKey === urlKey) {
      arrival.current = null;
      return;
    }
    if (urlKey && stateKey === arrival.current) return; // l'URL d'arrivée va être appliquée
    const next = new URLSearchParams(params);
    for (const k of KEYS) next.delete(k);
    for (const [k, v] of Object.entries(toParams(s))) next.set(k, v);
    setParams(next, { replace: !urlKey });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateKey]);
}
